/**
 * contractsApi.js
 * ───────────────
 * Client for the Contracts endpoints (Engine 1 backend, TEST-verified).
 *   getContracts    → POST /get_contracts   (read; POST-with-body, NOT a GET)
 *   createContract  → POST /manage_contract  { action:'create', … }   (Phase 2)
 *   updateContract  → POST /manage_contract  { action:'update', … }   (Phase 2)
 *
 * caller_email rides the query string (URLSearchParams auto-encodes '#' → '%23');
 * everything else rides the JSON body — exactly what the handlers expect
 * (caller_email from req.params, contract fields from the body). Mirrors the
 * createSite fetch pattern: throw on !res.ok carrying the backend's err.error.
 */

import { apiFetch } from '../../../auth/apiFetch';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:7071/api';

/**
 * List contracts visible to the caller (site-scoped server-side). Optional
 * { contractId } narrows to one. get_contracts is POST-with-body — pass {} for
 * the full scoped set. Returns the parsed { contracts: [...] } envelope.
 */
export async function getContracts(callerEmail, { contractId } = {}) {
  const params = new URLSearchParams();
  if (callerEmail) params.append('caller_email', callerEmail);
  const body = contractId ? { contract_id: contractId } : {};
  const res = await apiFetch(`${API_BASE_URL}/get_contracts?${params.toString()}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `API error: ${res.status}`);
  }
  return res.json();
}

/**
 * Create a contract. contract_id is supplied by the caller and is immutable
 * post-create (it is the locator). dwell_auto_disposition defaults false server
 * side. 409 on duplicate contract_id within the site. (Authored now; USED in Phase 2.)
 */
export async function createContract(
  callerEmail,
  { site_id, contract_id, customer_po, dwell_auto_disposition },
) {
  const params = new URLSearchParams();
  if (callerEmail) params.append('caller_email', callerEmail);
  const body = { action: 'create', site_id, contract_id };
  if (customer_po !== undefined) body.customer_po = customer_po;
  if (dwell_auto_disposition !== undefined) body.dwell_auto_disposition = dwell_auto_disposition;
  const res = await apiFetch(`${API_BASE_URL}/manage_contract?${params.toString()}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `API error: ${res.status}`);
  }
  return res.json();
}

/**
 * Update a contract — located by contract_id + site_id (both required).
 * Patches only the fields supplied (customer_po / dwell_auto_disposition);
 * contract_id is the locator and is never changed. (Authored now; USED in Phase 2.)
 */
export async function updateContract(
  callerEmail,
  { contract_id, site_id, customer_po, dwell_auto_disposition },
) {
  const params = new URLSearchParams();
  if (callerEmail) params.append('caller_email', callerEmail);
  const body = { action: 'update', contract_id, site_id };
  if (customer_po !== undefined) body.customer_po = customer_po;
  if (dwell_auto_disposition !== undefined) body.dwell_auto_disposition = dwell_auto_disposition;
  const res = await apiFetch(`${API_BASE_URL}/manage_contract?${params.toString()}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `API error: ${res.status}`);
  }
  return res.json();
}

/**
 * Delete a contract — located by contract_id + site_id (both required). HARD-BLOCKED
 * server-side (409) if any WarehouseItem still references the contract. contract_id +
 * site_id are the immutable doc-id/PK pair, so delete-then-recreate is the only way to
 * correct a mistyped contract_id.
 */
export async function deleteContract(callerEmail, { contract_id, site_id }) {
  const params = new URLSearchParams();
  if (callerEmail) params.append('caller_email', callerEmail);
  const body = { action: 'delete', contract_id, site_id };
  const res = await apiFetch(`${API_BASE_URL}/manage_contract?${params.toString()}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `API error: ${res.status}`);
  }
  return res.json();
}
